/**
 * AdminLogin — Login dedicato al backoffice Admin.
 *
 * Flusso email/password separato dal PIN Staff. Deve essere l'UNICA porta
 * di accesso a /admin. Nessun redirect al PIN keypad.
 *
 * - Se l'utente è già autenticato con ruolo manager+ → nav /admin.
 * - Se ruolo insufficiente (staff base) → messaggio dedicato.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, canAccess } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, ShieldCheck } from "lucide-react";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && canAccess(user, "manager")) {
      const target = loc.state?.from || "/admin";
      nav(target, { replace: true });
      return;
    }
    // Meta reviewer (o utente con SOLO admin.integrations): redirect diretto
    // alla sua unica pagina consentita.
    if (user && user.role === "meta_reviewer") {
      nav("/admin/integrations", { replace: true });
    }
  }, [user, nav, loc.state]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      // Meta reviewer path: consentito senza minRole=manager.
      if (u.role === "meta_reviewer") {
        toast.success(`Benvenuto ${u.name || u.email}`);
        nav("/admin/integrations", { replace: true });
        return;
      }
      if (!canAccess(u, "manager")) {
        toast.error("Accesso Admin negato: ruolo insufficiente.");
        return;
      }
      toast.success(`Benvenuto ${u.name || u.email}`);
      nav(loc.state?.from || "/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-sand-100 flex items-center justify-center px-4 py-8"
          data-testid="admin-login-page">
      <form onSubmit={submit}
             className="w-full max-w-sm bg-white rounded-3xl border border-sand-300 p-6 space-y-4">
        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl bg-bronze-50 border border-bronze-200
                            flex items-center justify-center mx-auto">
            <ShieldCheck className="w-7 h-7 text-bronze-700" strokeWidth={1.5} />
          </div>
          <div className="eyebrow mt-3">Sarabi</div>
          <h1 className="font-serif text-3xl mt-1">Backoffice</h1>
          <p className="text-xs text-ink-500 mt-1">
            Accesso dedicato a manager, admin e super admin.
          </p>
        </div>

        <div>
          <Label>Email</Label>
          <Input type="email" value={email} required autoComplete="username"
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-login-email" className="h-11" />
        </div>
        <div>
          <Label>Password</Label>
          <Input type="password" value={password} required autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-login-password" className="h-11" />
        </div>

        <Button type="submit" className="w-full h-11" disabled={busy}
                 data-testid="admin-login-submit">
          <LogIn className="w-4 h-4 mr-2" />
          {busy ? "Accesso in corso…" : "Entra"}
        </Button>

        <div className="text-center text-xs text-ink-500 pt-2 border-t border-sand-200">
          Sei un operatore di sala?{" "}
          <Link to="/staff/login" className="underline text-bronze-700"
                 data-testid="admin-login-goto-staff">
            Vai al PIN Staff
          </Link>
        </div>
      </form>
    </div>
  );
}
